import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { format, parseISO } from "date-fns";
import { useBulkProgram } from "@/hooks/useBulkCelebrations";
import { isBulkProgramActive } from "@/lib/bulkCelebrations";

type Props = {
  companyId: string;
};

export function BulkOrdersSection({ companyId }: Props) {
  const programQuery = useBulkProgram(companyId);
  const active = isBulkProgramActive(programQuery.data ?? null);

  const runsQuery = useQuery({
    queryKey: ["bulk-runs-list", companyId],
    enabled: active,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("bulk_celebration_runs")
        .select("id, period_key, period_start, period_end, delivery_date, status, order_id, orders(reference, payment_status)")
        .eq("company_id", companyId)
        .order("period_start", { ascending: false })
        .limit(12);
      if (error) throw error;
      return data ?? [];
    },
  });

  if (!active) return null;
  const runs = runsQuery.data ?? [];
  if (runs.length === 0 && !runsQuery.isLoading) {
    return (
      <section className="mb-6 rounded-xl border border-charcoal/10 bg-white p-4">
        <h2 className="text-sm font-semibold text-navy">Bulk celebrations</h2>
        <p className="mt-1 text-sm text-charcoal/55">
          No bulk periods yet.{" "}
          <Link to="/settings/program#bulk" className="underline">
            Configure in Settings
          </Link>
        </p>
      </section>
    );
  }

  return (
    <section className="mb-6 rounded-xl border border-charcoal/10 bg-white p-4">
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-sm font-semibold text-navy">Bulk celebrations</h2>
        <Link to="/settings/program#bulk" className="text-xs text-charcoal/50 underline">
          Settings
        </Link>
      </div>
      <ul className="mt-3 space-y-2">
        {runs.map((run) => {
          const order = run.orders as { reference?: string; payment_status?: string } | null;
          return (
            <li
              key={run.id}
              className="flex flex-wrap items-center justify-between gap-2 rounded-lg border px-3 py-2 text-sm"
            >
              <span>
                <span className="font-medium">{run.period_key}</span>
                <span className="text-charcoal/45">
                  {" "}
                  · {format(parseISO(run.period_start), "d MMM")} –{" "}
                  {format(parseISO(run.period_end), "d MMM yyyy")}
                </span>
              </span>
              <span className="text-charcoal/55 capitalize">
                {run.status.replace(/_/g, " ")}
                {order?.reference ? ` · ${order.reference}` : ""}
              </span>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
